import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Typography, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      console.log(
        this.state.error,
        this.state.errorInfo?.componentStack,
      );
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-6 max-w-2xl mx-auto">
          <Alert
            type="error"
            message="Something went wrong"
            description={
              <Space direction="vertical" className="w-full">
                <Text>
                  An error occurred in the application. Please try refreshing the page or click the button below to reset the component.
                </Text>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <>
                    <Text type="danger" className="font-mono text-sm">
                      {this.state.error.toString()}
                    </Text>
                    {this.state.errorInfo && (
                      <Text type="danger" className="font-mono text-sm whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </Text>
                    )}
                  </>
                )}
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={this.handleReset}
                >
                  Try Again
                </Button>
              </Space>
            }
            className="w-full"
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 